import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  MetaDataInterface,
  StudentResultInterface,
} from "../../Constants/Interfaces/bodyInterface/studentResult.interface";
import { RESULT_STATUS } from "../../Constants/Enums/Results.enum";
import { computeGradeFromMarks, parseStudentMetadata } from "../../Services/results/results";
import { getValue } from "../../utils/object.utils";

const initialState = {
  selectedStudentForResult: {
    avatar: null,
    first_name: "",
    last_name: "",
    full_name: "",
    gender: "",
    roll_no: "",
    result_status: RESULT_STATUS.PENDING,
    result_entries: [],
    remarks: "",
    metadata: {},
    link: null,
  } as StudentResultInterface,
  pageNo: 1,
  paginatedStudentResultList: [] as any,
  dirtyStudentIds: [] as number[],
  dirtyRevisions: {} as Record<number, number>,
};

const markStudentDirty = (state: typeof initialState, studentId: number) => {
  if (!state.dirtyStudentIds.includes(studentId)) {
    state.dirtyStudentIds.push(studentId);
  }
  state.dirtyRevisions[studentId] = (state.dirtyRevisions[studentId] || 0) + 1;
};

export const studentResultSlice = createSlice({
  name: "studentResult",
  initialState,
  reducers: {
    setSelectedStudentForResult: (
      state,
      action: PayloadAction<StudentResultInterface>,
    ) => {
      state.selectedStudentForResult = action.payload;
    },

    updateMarksInResultEntry: (
      state,
      action: PayloadAction<{ subjectId: number; value: number }>,
    ) => {
      const resultEntry = state.selectedStudentForResult.result_entries;

      const subjectId = action.payload.subjectId;

      const updatingEntry = resultEntry.find(
        ({ subject_id: entrySubjectId }) => subjectId === entrySubjectId,
      );

      if (!updatingEntry) return;
      const fullMarks = getValue(updatingEntry, "full_marks", 100);
      const marks = Number(action.payload.value);

      const grade = computeGradeFromMarks({ marks, fullMarks });

      updatingEntry.marks = marks;
      updatingEntry.grade = grade;
    },

    updateGradeInResultEntry: (
      state,
      action: PayloadAction<{ subjectId: number; value: string }>,
    ) => {
      const resultEntry = state.selectedStudentForResult.result_entries;

      const subjectId = action.payload.subjectId;

      const updatingEntry = resultEntry.find(
        ({ subject_id: entrySubjectId }) => subjectId === entrySubjectId,
      );

      if (!updatingEntry) return;

      updatingEntry.grade = action.payload.value;
    },

    updatePageNo: (state, action: PayloadAction<number>) => {
      state.pageNo = action.payload;
    },

    setPaginatedStudentResultList: (state, action: PayloadAction<any>) => {
      const students = getValue(action.payload, "result", []);
      state.paginatedStudentResultList = {
        ...action.payload,
        result: Array.isArray(students)
          ? students.map((student: any) => ({
              ...student,
              metadata: parseStudentMetadata(student?.metadata),
            }))
          : students,
      };
      // Fresh data from the server means no local edits are pending anymore.
      state.dirtyStudentIds = [];
      state.dirtyRevisions = {};
    },

    setRemarks: (state, action: PayloadAction<string>) => {
      state.selectedStudentForResult.remarks = action.payload;
    },

    setMetaData: (state, action: PayloadAction<MetaDataInterface>) => {
      state.selectedStudentForResult.metadata = action.payload;
    },

    // --- Spreadsheet-style editing: operate directly on a student inside
    // paginatedStudentResultList by student_id, instead of requiring a
    // single "selected student". Save/payload logic is untouched - these
    // reducers only mutate the same shape the save handler already reads.
    updateMarksInResultEntryForStudent: (
      state,
      action: PayloadAction<{
        studentId: number;
        subjectId: number;
        value: number;
      }>,
    ) => {
      const { studentId, subjectId, value } = action.payload;
      const students = getValue(state.paginatedStudentResultList, "result", []);
      const student = students.find(
        (s: any) => s.student_id === studentId,
      );
      if (!student) return;

      const updatingEntry = (student.result_entries || []).find(
        (entry: any) => entry.subject_id === subjectId,
      );
      if (!updatingEntry) return;

      const fullMarks = getValue(updatingEntry, "full_marks", 100);
      const marks = Number(value);
      const grade = computeGradeFromMarks({ marks, fullMarks });

      updatingEntry.marks = marks;
      updatingEntry.grade = grade;

      markStudentDirty(state, studentId);
    },

    updateGradeInResultEntryForStudent: (
      state,
      action: PayloadAction<{
        studentId: number;
        subjectId: number;
        value: string;
      }>,
    ) => {
      const { studentId, subjectId, value } = action.payload;
      const students = getValue(state.paginatedStudentResultList, "result", []);
      const student = students.find(
        (s: any) => s.student_id === studentId,
      );
      if (!student) return;

      const updatingEntry = (student.result_entries || []).find(
        (entry: any) => entry.subject_id === subjectId,
      );
      if (!updatingEntry) return;

      updatingEntry.grade = value;

      markStudentDirty(state, studentId);
    },

    updateMetaDataForStudent: (
      state,
      action: PayloadAction<{
        studentId: number;
        field: "height" | "weight" | "attendance";
        value: number;
      }>,
    ) => {
      const { studentId, field, value } = action.payload;
      const students = getValue(state.paginatedStudentResultList, "result", []);
      const student = students.find(
        (s: any) => s.student_id === studentId,
      );
      if (!student) return;

      student.metadata = {
        ...parseStudentMetadata(student.metadata),
        [field]: value,
      };

      markStudentDirty(state, studentId);
    },

    updateRemarksForStudent: (
      state,
      action: PayloadAction<{ studentId: number; value: string }>,
    ) => {
      const { studentId, value } = action.payload;
      const students = getValue(state.paginatedStudentResultList, "result", []);
      const student = students.find(
        (s: any) => s.student_id === studentId,
      );
      if (!student) return;

      student.remarks = value;

      markStudentDirty(state, studentId);
    },

    markStudentResultSaved: (
      state,
      action: PayloadAction<{ studentId: number }>,
    ) => {
      state.dirtyStudentIds = state.dirtyStudentIds.filter(
        (id) => id !== action.payload.studentId,
      );
      delete state.dirtyRevisions[action.payload.studentId];
    },

    clearDirtyStudentIds: (state) => {
      state.dirtyStudentIds = [];
      state.dirtyRevisions = {};
    },
  },
});
export const {
  setSelectedStudentForResult,
  updateMarksInResultEntry,
  updateGradeInResultEntry,
  updatePageNo,
  setRemarks,
  setMetaData,
  setPaginatedStudentResultList,
  updateMarksInResultEntryForStudent,
  updateGradeInResultEntryForStudent,
  updateMetaDataForStudent,
  updateRemarksForStudent,
  markStudentResultSaved,
  clearDirtyStudentIds,
} = studentResultSlice.actions;
export const studentResultReducer = studentResultSlice.reducer;
